import { useState } from 'react';
import { Text, Button, Switch, Loader } from '@ui-component-library';
import { updateUserHubspotSubscriptions } from '../../../../api/services/user';

type HubspotData = {
    hubspot_user_subscriptions?: Array<{ subscription_id: string; subscription_status: string }>;
    hubspot_subscription_definitions?: Array<{ subscription_id: string; name_sv: string; description: string; name: string }>;
};

type EmailSettingsProps = {
    hubspotData?: HubspotData | null;
    onHubspotDataRefresh: () => void;
};

type HubspotSubscription = {
    subscription_id: string;
    subscription_status: string;
};

type HubspotSubscriptionDefinition = {
    subscription_id: string;
    name_sv: string;
    description: string;
    name: string;
    isActive:boolean;
};

export default function EmailSettings({ hubspotData, onHubspotDataRefresh }: EmailSettingsProps) {
    const [hubspotSubscriptions, setHubspotSubscriptions] = useState<Record<string, boolean>>({});
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);

    const definitions = (Array.isArray(hubspotData?.hubspot_subscription_definitions)
        ? hubspotData.hubspot_subscription_definitions
        : []) as HubspotSubscriptionDefinition[];


    const isLoading = hubspotData === null || hubspotData?.hubspot_subscription_definitions === undefined;

    const nameToId = Object.fromEntries(
        definitions.map((def) => [def.name_sv, def.subscription_id])
    );

    const getSubscriptionInfo = (nameSv: string) => {
        const subs = (hubspotData?.hubspot_user_subscriptions as HubspotSubscription[]) ?? [];
        const subId = nameToId[nameSv];
        const currentSub = subs.find((s) => s.subscription_id === subId);
        const isSubscribed = currentSub?.subscription_status === 'SUBSCRIBED' || currentSub?.subscription_status === 'NOT_SPECIFIED';
        return { subId, isSubscribed };
    };

    const hasHubspotChanges = () =>
        definitions.some((def) => {
            const { isSubscribed } = getSubscriptionInfo(def.name_sv);
            return def.name_sv in hubspotSubscriptions && hubspotSubscriptions[def.name_sv] !== isSubscribed;
        });

    const handleSubmit = async () => {
        const toSubmit: { subscription_id: string; subscribed: boolean }[] = [];

        definitions.forEach((def) => {
            const { subId, isSubscribed } = getSubscriptionInfo(def.name_sv);
            if (def.name_sv in hubspotSubscriptions && hubspotSubscriptions[def.name_sv] !== isSubscribed && subId) {
                toSubmit.push({ subscription_id: subId, subscribed: hubspotSubscriptions[def.name_sv] });
            }
        });

        if (toSubmit.length > 0) {
            try {
                const res = await updateUserHubspotSubscriptions({ hubspot_subscriptions: toSubmit });
                if (res.ok) {
                    setSuccess(true);
                    setTimeout(() => setSuccess(false), 1000);
                    onHubspotDataRefresh();
                } else {
                    setError(true);
                    setTimeout(() => setError(false), 2000);
                }
            } catch (err) {
                setError(true);
                setTimeout(() => setError(false), 2000);
                console.error(err);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-[700px] mx-auto flex flex-col items-center justify-center py-16">
                <Loader />
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600 mt-4">
                    Laddar e-postinställningar...
                </Text>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[700px] mx-auto">

            <section className=" p-4">
                <div className="p-6 border-b border-cloud-200 bg-cloud-50/30">
                    <Text variant="text-base/leading-normal/medium" className="text-charcoal-1000">
                        Dina e-postinställningar
                    </Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                        Hantera dina e-postinställningar.
                    </Text>
                </div>
                {success && (
                    <div className="rounded-lg bg-success-100 border border-success-300 px-4 py-3 mb-1 text-sm text-success-800">
                        Din information har sparats.
                    </div>
                )}
                {error && (
                    <div className="rounded-lg bg-danger-500 border border-danger-500 px-4 py-3 mb-1 text-sm text-danger-1000">
                        Ett fel uppstod när vi försökte spara dina inställningar. Vänligen försök igen.
                    </div>
                )}

                <div className="divide-y divide-cloud-200 space-y-2">
                    <div className="rounded-lg bg-white-50 p-6 flex items-center justify-between">
                        <div className="flex flex-col">
                            <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                                Nödvändig e-post
                            </Text>
                            <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                E-post som är kritiskt för plattformens funktioner.
                            </Text>
                        </div>
                        <Switch checked={true} disabled={true} onChange={() => { }} />
                    </div>
                    {definitions.filter(d=>d.isActive).map((subscription) => {
                        const { isSubscribed } = getSubscriptionInfo(subscription.name_sv);
                        const checked =
                            subscription.name_sv in hubspotSubscriptions
                                ? hubspotSubscriptions[subscription.name_sv]
                                : isSubscribed;

                        return (
                            <div
                                key={subscription.subscription_id}
                                className="rounded-lg bg-white-50 p-6 flex items-center justify-between"
                            >
                                <div className="flex flex-col">
                                    <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                                        {subscription.name_sv}
                                    </Text>
                                    <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                                        {subscription.description}
                                    </Text>
                                </div>
                                <Switch
                                    checked={checked}
                                    onChange={(newChecked) => {
                                        setHubspotSubscriptions((prev) => ({
                                            ...prev,
                                            [subscription.name_sv]: newChecked,
                                        }));
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </section>
            <div className="flex justify-end px-4">
                <Button
                    tw
                    variant="primary"
                    onClick={() => handleSubmit()}
                    disabled={!hasHubspotChanges()}
                >
                    Spara uppgifter
                </Button>
            </div>
        </div>
    );
}